import { Hub, Auth, Amplify } from 'aws-amplify'
import logger from '../lib/logger'
import { isProd, isLocal } from '../lib/helpers'
import { beginCheckoutFlow } from '~/lib/helpers/cart'
import {
  parseCustomOauthState,
  parseRawState,
} from '~/lib/helpers/parse-custom-oauth-state'
import { useUser } from '~/lib/hooks'

// Amplify.Logger.LOG_LEVEL = 'DEBUG'

export default function({ store, app, route, $api }, inject) {
  logger.debug(`amplify: Configuring project`)

  const domain = isProd()
    ? 'oauth.logo.com'
    : 'logo.auth.us-east-2.amazoncognito.com'

  const redirect_domain =
    isLocal() || window.location.host.indexOf('localhost') >= 0
      ? 'http://localhost:8080'
      : `https://${window?.location.host}`

  const user_pool_id = store.state.enterprise.pool_id
  const client_id = store.state.enterprise.pool_client_id
  const region = store.state.enterprise.pool_region

  const config = {
    region: region,
    userPoolId: user_pool_id,
    userPoolWebClientId: client_id,
    oauth: {
      domain: domain,
      scope: [
        'phone',
        'email',
        'openid',
        'profile',
        'aws.cognito.signin.user.admin',
      ],
      clientId: client_id,
      redirectSignIn: `${redirect_domain}/oauth/sign-in/`,
      redirectSignOut: `${redirect_domain}/oauth/sign-out/`,
      responseType: 'code',
    },
  }

  Amplify.configure({ Auth: config })

  const getAuthInstance = () => Promise.resolve(Auth)
  inject('getAuthInstance', getAuthInstance)

  let isHandlingDoubleSignIn = false

  let provider = 'email'
  let isSignupSession = false

  Hub.listen(/.*/, async ({ payload: { event, data } }) => {
    logger.debug(`Amplify event: ${event}.  Data received: `, data)

    if (event === 'signUp') {
      isSignupSession = true
    }

    if (event === 'signIn') {
      const { getUser } = useUser($api)
      const user = await getUser()

      if (isSignupSession) {
        app.$bus.$emit(app.$events.UserRegistered, {
          user,
          enterprise: store.state.enterprise,
          method: provider,
        })
      } else {
        app.$bus.$emit(app.$events.UserSignedIn, {
          user,
          enterprise: store.state.enterprise,
          method: provider,
        })
      }
    }

    if (event === 'signOut') {
      app.$bus.$emit(app.$events.UserSignedOut, {
        enterprise: store.state.enterprise,
      })

      isSignupSession = false
    }

    if (event === 'signIn_failure') {
      // Cognito will put itself into an infinite loop here if we don't manage
      // a return early here
      if (isHandlingDoubleSignIn) return

      // This can occasionally get called without the route, so make sure it's loaded.
      if (!route?.query?.error_description) return

      isHandlingDoubleSignIn = true

      let isLinkingError = route.query.error_description.startsWith(
        'Already found an entry for username'
      )

      const state = route.query.state
        ? parseCustomOauthState(parseRawState(route.query.state))
        : {}

      if (!isLinkingError) {
        return app.router.replace({
          ...state,
          path: state.return,
        })
      }

      isSignupSession = false

      return Auth.federatedSignIn({
        provider: state.provider,
        customState: new URLSearchParams({
          ...state,
        }).toString(),
      })
    }

    if (event === 'customOAuthState') {
      // FIXME: Refactor to nuxt methods
      // eslint-disable-next-line no-case-declarations
      const query = parseCustomOauthState(data)

      // eslint-disable-next-line no-case-declarations
      let app_return_path = query.app_return_path || '/dashboard/latest'

      if (app_return_path[0] !== '/') {
        app_return_path = '/' + app_return_path
      }

      provider = query.provider
      if (query.action === 'sign_up') {
        isSignupSession = true
      }

      if (query.checkout) {
        try {
          await beginCheckoutFlow({
            $api: app.$api,
            store: app.store,
            router: app.router,
            cancel_page: '/cart',
            $getAuthInstance: this.$getAuthInstance,
          })

          return
        } catch (e) {
          const message =
            e.response?.data.error === 'invalid_request'
              ? e.response.data.message
              : `We couldn't create a checkout session.  Please review your cart, and try again.`

          app.$toast.error(message)
        }
      }

      app.router.replace({
        path: app_return_path,
        query: Object.assign(query, {
          app_return_path: undefined,
          return: undefined,
        }),
      })
    }
  })
}
